import {
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
  select,
} from '@ngrx/store';
import { pipe } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { AvailableArea } from 'src/app/models/available-areas/available-area';
import { Coordinate } from 'src/app/models/coordinate/coordinate';
import { RestaurantKey } from 'src/app/models/restaurant/restaurant-key';
import { Restaurants } from 'src/app/models/restaurant/restaurants';
import { SearchAreaGroup } from 'src/app/models/search-area-group/search-area-group';
import { SearchAreas } from 'src/app/models/search-area/search-areas';
import { Time } from 'src/app/models/time/time';
import { AllActionsUnion } from '../../../app.actions';
import { makeSearchParams } from '../../shared/search/make-search-params';
import { DEFAULT_SEATS, DEFAULT_ZOOM } from '../constants';
import {
  clearRestaurants,
  hideAreaSelectModal,
  restoreSearchState,
  selectCurrentPositionInAreaSelectModal,
  selectSearchAreaInAreaSelectModal,
  showAreaSelectModal,
  storeAvailableAreas,
  storeCenterPosition,
  storeCurrentPosition,
  storeCurrentViewMode,
  storeReservableTimes,
  storeRestaurants,
  storeSearchAreaGroups,
  storeSearchAreas,
  storeSeats,
  storeSelectedReservableTime,
  storeSelectedRestaurantKey,
  storeSelectedSearchAreaGroupKey,
  storeSelectedSearchAreakey,
  storeZoom,
  updateCurrentPositionState,
  updateRestaurantsLoadingState,
} from './search.actions';
import { ViewMode } from './view-mode';

export type CurrentPositionState =
  | 'none'
  | 'loading'
  | 'available'
  | 'unAvailable'
  | 'outOfArea';
export type RestaurantsLoadingState = 'none' | 'loading' | 'loaded';

export interface SearchState {
  centerPosition: Coordinate;
  currentPosition: Coordinate | null;
  currentPositionState: CurrentPositionState;
  zoom: number;
  availableAreas: AvailableArea[];
  currentViewMode: ViewMode;
  restaurantsLoadingState: RestaurantsLoadingState;
  restaurants: Restaurants;
  selectedRestaurantKey: RestaurantKey | null;
  searchAreaGroups: SearchAreaGroup[];
  searchAreas: { [searchAreaGroupKey: string]: SearchAreas };
  selectedSearchAreaGroupKey: string | null;
  selectedSearchAreaKey: string | null;
  reservableTimes: Time[];
  selectedReservableTime: string;
  seats: number;
  isShowingAreaSelectModal: boolean;
}

const initialState: SearchState = {
  centerPosition: new Coordinate(),
  currentPosition: null,
  currentPositionState: 'none',
  zoom: DEFAULT_ZOOM,
  availableAreas: [],
  currentViewMode: ViewMode.Map,
  restaurantsLoadingState: 'none',
  restaurants: new Restaurants([]),
  selectedRestaurantKey: null,
  searchAreaGroups: [],
  searchAreas: {},
  selectedSearchAreaGroupKey: null,
  selectedSearchAreaKey: null,
  reservableTimes: [],
  selectedReservableTime: '',
  seats: DEFAULT_SEATS,
  isShowingAreaSelectModal: false,
};

export const featureName = 'searchFeature';

export function searchReducer(
  state_: SearchState | undefined,
  action: AllActionsUnion,
): SearchState {
  const reducer_ = createReducer<SearchState>(
    initialState,
    on(
      updateRestaurantsLoadingState,
      (state, { payload }): SearchState => {
        return { ...state, restaurantsLoadingState: payload };
      },
    ),
    on(
      updateCurrentPositionState,
      (state, { payload }): SearchState => {
        return { ...state, currentPositionState: payload };
      },
    ),
    on(
      storeSearchAreaGroups,
      (state, { payload }): SearchState => {
        return { ...state, searchAreaGroups: payload };
      },
    ),
    on(
      storeSearchAreas,
      (state, { payload }): SearchState => {
        return {
          ...state,
          searchAreas: {
            ...state.searchAreas,
            [payload.searchAreaGroupKey]: payload.searchAreas,
          },
        };
      },
    ),
    on(
      storeAvailableAreas,
      (state, { payload }): SearchState => {
        return { ...state, availableAreas: payload };
      },
    ),
    on(
      storeCurrentPosition,
      (state, { payload }): SearchState => {
        return {
          ...state,
          currentPosition: payload,
          centerPosition: payload,
          zoom: DEFAULT_ZOOM,
        };
      },
    ),
    on(
      storeCenterPosition,
      (state, { payload }): SearchState => {
        return { ...state, centerPosition: payload };
      },
    ),
    on(
      storeZoom,
      (state, { payload }): SearchState => {
        return {
          ...state,
          zoom: payload,
        };
      },
    ),
    on(
      storeSelectedSearchAreakey,
      (state, { payload }): SearchState => {
        return {
          ...state,
          selectedSearchAreaKey: payload,
        };
      },
    ),
    on(
      storeSeats,
      (state, { payload }): SearchState => {
        return {
          ...state,
          seats: payload,
        };
      },
    ),
    on(
      storeCurrentViewMode,
      (state, { payload }): SearchState => {
        return { ...state, currentViewMode: payload };
      },
    ),
    on(
      storeRestaurants,
      (state, { payload }): SearchState => {
        return { ...state, restaurants: payload };
      },
    ),
    on(
      storeSelectedRestaurantKey,
      (state, { payload }): SearchState => {
        return { ...state, selectedRestaurantKey: payload };
      },
    ),
    on(
      clearRestaurants,
      (state): SearchState => {
        return {
          ...state,
          restaurantsLoadingState: 'none',
          restaurants: new Restaurants([]),
          selectedRestaurantKey: null,
        };
      },
    ),
    on(
      restoreSearchState,
      (state, { payload }): SearchState => {
        return {
          ...state,
          currentPosition: payload.currentPosition || null,
          currentViewMode: payload.viewMode || ViewMode.Map,
          seats: payload.seats || DEFAULT_SEATS,
          selectedReservableTime: payload.startsFromTime || '',
          selectedSearchAreaGroupKey: payload.searchAreaGroupKey || null,
          selectedSearchAreaKey: payload.searchAreaKey || null,
        };
      },
    ),
    on(
      storeReservableTimes,
      (state, { payload }): SearchState => {
        return {
          ...state,
          reservableTimes: payload,
        };
      },
    ),
    on(
      storeSelectedReservableTime,
      (state, { payload }): SearchState => {
        return {
          ...state,
          selectedReservableTime: payload,
        };
      },
    ),
    on(
      storeSelectedSearchAreaGroupKey,
      (state, { payload }): SearchState => {
        return {
          ...state,
          selectedSearchAreaGroupKey: payload,
        };
      },
    ),
    on(
      showAreaSelectModal,
      (state): SearchState => {
        return {
          ...state,
          isShowingAreaSelectModal: true,
        };
      },
    ),
    on(
      hideAreaSelectModal,
      (state, { payload }): SearchState => {
        return {
          ...state,
          selectedSearchAreaGroupKey: payload.searchAreaGroupKey,
          isShowingAreaSelectModal: false,
        };
      },
    ),
    on(
      selectCurrentPositionInAreaSelectModal,
      (state): SearchState => {
        return {
          ...state,
          selectedSearchAreaGroupKey: null,
          selectedSearchAreaKey: null,
          currentViewMode: ViewMode.Map,
          isShowingAreaSelectModal: false,
        };
      },
    ),
    on(
      selectSearchAreaInAreaSelectModal,
      (state, { payload }): SearchState => {
        return {
          ...state,
          selectedSearchAreaKey: payload.selectedSearchAreaKey,
          currentViewMode: ViewMode.List,
          isShowingAreaSelectModal: false,
        };
      },
    ),
  );
  return reducer_(state_, action);
}

// selectors
const getFeature = createFeatureSelector<SearchState>(featureName);
const selectSearchAreaGroup = createSelector(
  getFeature,
  v =>
    v.searchAreaGroups.find(
      group => group.key === v.selectedSearchAreaGroupKey,
    ) || null,
);
const selectSearchAreas = createSelector(
  getFeature,
  v => v.searchAreas[v.selectedSearchAreaGroupKey || ''] || new SearchAreas([]),
);
const selectSearchArea = createSelector(
  getFeature,
  selectSearchAreas,
  (state, searchAreas) =>
    searchAreas.findByKey(state.selectedSearchAreaKey || ''),
);

export const getRestaurantsLoadingState = pipe(
  select(
    createSelector(
      getFeature,
      v => v.restaurantsLoadingState,
    ),
  ),
  distinctUntilChanged(),
);

export const getCurrentPositionState = pipe(
  select(
    createSelector(
      getFeature,
      v => v.currentPositionState,
    ),
  ),
  distinctUntilChanged(),
);

export const getState = pipe(
  select(
    createSelector(
      getFeature,
      v => v,
    ),
  ),
);

export const getSearchAreaGroups = pipe(
  select(
    createSelector(
      getFeature,
      v => v.searchAreaGroups,
    ),
  ),
  distinctUntilChanged((a, b) => a.every((v, i) => v.eq(b[i]))),
);

export const getSelectedSearchAreaGroup = pipe(
  select(selectSearchAreaGroup),
  distinctUntilChanged((a, b) => {
    if (a === null || b === null) {
      return false;
    }
    return a.eq(b);
  }),
);

export const getSelectedSearchAreas = pipe(
  select(selectSearchAreas),
  distinctUntilChanged((a, b) => a.eq(b)),
);

export const getSelectedSearchArea = pipe(
  select(selectSearchArea),
  distinctUntilChanged((a, b) => {
    if (a === null || b === null) {
      return false;
    }
    return a.eq(b);
  }),
);

export const getSeats = pipe(
  select(
    createSelector(
      getFeature,
      v => v.seats,
    ),
  ),
  distinctUntilChanged(),
);

export const getAvailableAreas = pipe(
  select(
    createSelector(
      getFeature,
      v => v.availableAreas,
    ),
  ),
  distinctUntilChanged((a, b) => a.every((v, i) => v.name === b[i].name)),
);

export const getCurrentPosition = pipe(
  select(
    createSelector(
      getFeature,
      v => v.currentPosition,
    ),
  ),
);

export const getCenterPosition = pipe(
  select(
    createSelector(
      getFeature,
      v => v.centerPosition,
    ),
  ),
);

export const getZoom = pipe(
  select(
    createSelector(
      getFeature,
      v => v.zoom,
    ),
  ),
  distinctUntilChanged(),
);

export const getReservableTimes = pipe(
  select(
    createSelector(
      getFeature,
      v => v.reservableTimes,
    ),
  ),
  distinctUntilChanged((a, b) => a.every((v, i) => v.eq(b[i]))),
);

export const getSelectedReservableTime = pipe(
  select(
    createSelector(
      getFeature,
      v => v.selectedReservableTime,
    ),
  ),
  distinctUntilChanged(),
);

export const getCurrentViewMode = pipe(
  select(
    createSelector(
      getFeature,
      v => v.currentViewMode,
    ),
  ),
  distinctUntilChanged(),
);

export const getRestaurants = pipe(
  select(
    createSelector(
      getFeature,
      v => v.restaurants,
    ),
  ),
  distinctUntilChanged((a, b) => a.eq(b)),
);

export const getSelectedRestaurant = pipe(
  select(
    createSelector(
      getFeature,
      v => v.restaurants.findByKey(v.selectedRestaurantKey),
    ),
  ),
  distinctUntilChanged((a, b) => {
    if (a === null || b === null) {
      return false;
    }

    return a.eq(b);
  }),
);

export const getSelectedRestaurantIndex = pipe(
  select(
    createSelector(
      getFeature,
      v => v.restaurants.getIndexByKey(v.selectedRestaurantKey),
    ),
  ),
  distinctUntilChanged(),
);

export const getSearchParams = pipe(
  select(
    createSelector(
      getFeature,
      selectSearchArea,
      selectSearchAreaGroup,
      (state, searchArea, searchAreaGroup) => {
        return makeSearchParams({
          seats: state.seats,
          currentPositionState: state.currentPositionState,
          currentPosition: state.currentPosition,
          centerPosition: state.centerPosition,
          startsFromTime: state.selectedReservableTime,
          searchArea,
          searchAreaGroup,
        });
      },
    ),
  ),
  distinctUntilChanged(),
);

export const getIsShowingAreaSelectModal = pipe(
  select(
    createSelector(
      getFeature,
      v => v.isShowingAreaSelectModal,
    ),
    distinctUntilChanged(),
  ),
);
